import { ConfigStateService } from '@medlogic/shared/state-config';
import { Control } from '../../model/control';
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CalculadoraService } from '../../service/calculadora.service';
import { CalculadoraConditionService } from '../../service/calculadora-condition.service';
import { MatDialog } from '@angular/material/dialog';
import { MultilineDialogComponent } from './multiline-dialog/multiline-dialog.component';
import { GlobalService, ConfigJsonService, IDialogData, EnTheme, IAtividadeComponenteDAL } from '@medlogic/shared/shared-interfaces';
import { Store } from '@ngrx/store';
import { selectMedicationAllSortByMedicationNameFilteredByPatientKeywordAndNoStopped } from '@medlogic/medlogic/medlogic-state';
import { loadMedications } from '@medlogic/medlogic/medlogic-state';
import { LibService } from '../../service/lib.service';
import { MsgPtBR } from '@medlogic/shared/shared-interfaces';
import { LogService } from '@medlogic/shared/shared-interfaces';
import { FormGroup } from '@angular/forms';
import { IVariable } from '../../interface/ivariable';
import { IBubble } from '../../interface/ibubble';
import { MatTableDataSource } from '@angular/material/table';

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'lib-ctr-multiline-textbox-label',
  templateUrl: './ctr-multiline-textbox-label.component.html',
  styleUrls: ['./ctr-multiline-textbox-label.component.css']
})
export class CtrMultilineTextboxLabelComponent extends Control implements OnInit {

  @Input() ctrl: IAtividadeComponenteDAL;
  @Input() formGroup = new FormGroup({});
  @Input() isLoading = true;
  @Input() isMobile: boolean;
  @Input() enTheme = EnTheme.default;

  @Output() onChangeNotify = new EventEmitter<IVariable>();
  /* Evento para permitir que os filhos, netos, etc, se comuniquem com todos os pais até a AtividadeView */
  @Output() eventBubble = new EventEmitter<IBubble>();

  public get isCompactVersion(): boolean {
    return this.ctrl ? this.ctrl?.Altura <= 20 : false;
  }

  public get hasContent(): boolean {
    return this.ctrl ? !this.global.isNullOrEmpty(this.ctrl?.ValorTexto) : false;
  }

  public get value(): string {
    const id = this.getId(this.ctrl);
    return this.formGroup.get(id).value;
  }

  public get isHtml(): boolean {
    try {
      if (!this.value) {
        return false;
      }
      return !this.global.IsNullOrEmpty(this.value) && (this.value.toString()).startsWith('<html>');
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'isHtml', error.message);
    }
    return false;
  }

  constructor(
    log: LogService,
    global: GlobalService,
    lib: LibService,
    cnf: ConfigStateService,
    cnfJson: ConfigJsonService,
    calc: CalculadoraService,
    calcCond: CalculadoraConditionService,
    dialog: MatDialog,
    msg: MsgPtBR,
    private store: Store<any>) {
      super(log, global, lib, cnf, cnfJson, calc, calcCond, dialog, msg);
  }

  ngOnInit(): void {
    try {
      if (this.showPrescriptionTable) {
        this.loadPrescriptions();
      }
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'ngOnInit', error.message);
    }
  }

  /**
   * Returns true when this control corresponds to variable 113839 (prescription view)
   */
  public get showPrescriptionTable(): boolean {
    try {
      const varNo = this.ctrl?.VariavelNo ?? null;
      return varNo === 113839;
    } catch (e) { return false; }
  }

  // Prescription table support
  public prescriptionRows: Array<{
    name: string;
    dosage: string;
    presentation: string;
    posology: string;
    firstDose?: string;
    personalizedTimes?: string[];
  }> = [];

  public displayedColumns: string[] = ['name', 'dosage', 'presentation', 'posology', 'firstDose', 'personalizedTimes'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);

  private didDispatchLoadMedications = false;

  /**
   * Load prescriptions from ctrl.Valor (or ValorTexto) and populate prescriptionRows
   * Expected source: this.ctrl.ValorTexto should contain prescription objects in JSON or a known format.
   * Here we'll try to parse JSON first; fallback: no rows.
   */
  protected loadPrescriptions(): void {
    try {
      this.subs.sink = this.store.select(selectMedicationAllSortByMedicationNameFilteredByPatientKeywordAndNoStopped)
        .subscribe((meds: any[]) => {
          try {
            this.prescriptionRows = [];

            if ((!meds || meds.length === 0) && !this.didDispatchLoadMedications) {
              this.didDispatchLoadMedications = true;

              try {
                this.store.dispatch(loadMedications({ numOfDaysToCheckOcurrency: 0 }));
              } catch (e) {
                this.log.Registrar(this.constructor.name, 'loadPrescriptions.dispatch', e.message);
              }

              return;
            }

            if (!meds || meds.length === 0) return;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            meds.filter(med => {
              try {
                const tempoUso = (med.tempoUso || med.tempoUsoDescricao || '').toString().toLowerCase();
                const dtEnd = med.dtEnd ? new Date(med.dtEnd) : null;

                if (tempoUso === 'ocasional') {
                  if (!dtEnd) { return false; }
                  return dtEnd >= today;
                }

                return true;
              } catch (e) { return false; }
            }).forEach(it => {
              try {
                const name = it.medicationName.toLowerCase()
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
                const dosage = it.dosage;
                const presentation = it.presentation;
                const posology = it.enPosology;
                const row = {
                  name,
                  dosage,
                  presentation,
                  posology,
                  firstDose: it.prescribedTime,
                  personalizedTimes: this.getCustomTimes(it)
                };

                this.prescriptionRows.push(row);
              } catch (e) {
                this.log.Registrar(this.constructor.name, 'loadPrescriptions.forEach', e.message);
              }
            });
            this.dataSource = new MatTableDataSource(this.prescriptionRows);
          } catch (e) {
            this.log.Registrar(this.constructor.name, 'loadPrescriptions.subscribe', e.message);
          }
        });

    } catch (error) {
      this.log.Registrar(this.constructor.name, 'loadPrescriptions', error.message);
    }
  }

  protected isActiveMedication(item: any): boolean {
    try {
      return !item.blInterrompido && (item.blInterrompido === false || item.blInterrompido === 0);
    } catch (e) { return false; }
  }

  protected isWithinTemporaryUse(usoTemporario: any): boolean {
    try {
      if (!usoTemporario) return true;

      const now = new Date();
      const inicio = usoTemporario.inicio ? new Date(usoTemporario.inicio) : null;
      const fim = usoTemporario.fim ? new Date(usoTemporario.fim) : null;

      if (inicio && now < inicio) return false;
      if (fim && now > fim) return false;

      return true;
    } catch (e) { return false; }
  }

  protected getCustomTimes(item: any) {
    try {
      if (!this.global.IsNullOrEmpty(item.horarios)) return item.horarios;

      return '';
    } catch (e) { return ''; }
  }

  /**
   * Botão de ampliar exibido quando a altura do componente é menor igual a 20.
   * Ao invés de exibir a textarea apenas exibe um botão com recurso de ampliação.
   */
  onAmpliar_Click($event: any): void {
    try {
      const dataDialog = {
        item: { value: this.ctrl?.ValorTexto, readOnly: this.ctrl?.CanEditItem }
      } as IDialogData;
      this.onWindowExecutionDialog(dataDialog, '95vw', '50vh');
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onAmpliar_Click', error.message);
    }
  }

  /* Abre a janela de ampliação, em formato editável, ou somente leitura. */
  protected onWindowExecutionDialog(dataDialog: IDialogData, width: string, height: string): void {
    try {
      const dialogRef = this.dialog.open(
        MultilineDialogComponent, {
        height,
        width,
        data: dataDialog
      });
      this.subs.sink = dialogRef
        .afterClosed()
        .subscribe(newValue => {
          try {
            if (newValue) {
              this.setValue(newValue);
            }
          } catch (error) {
            this.log.Registrar(this.constructor.name, 'onWindowExecutionDialog.subscribe', error.message);
          }
        });
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onWindowExecutionDialog', error.message);
    }
  }


}
